import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';
import { AlertSeverityEnum } from '@/shared/interfaces';

const { get, patch, put, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

export const useFetchSlaConfig = () => {
  return useQuery({
    queryKey: ['ticket-sla-config'],
    queryFn: async () => {
      return get<{ sla_habilitado: boolean }>(`${BASE_URL}configuracion/sla/`, true);
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useToggleSlaConfig = () => {
  const queryClient = useQueryClient();
  // El store expone setAlert(isVisible, message, severity), no showSnackbar.
  // Los valores del enum van en lowercase (success/error) — ver
  // errors.interface.ts.
  const setAlert = useUiStore(s => s.setAlert);

  return useMutation({
    mutationFn: async (sla_habilitado: boolean) => {
      return patch<{ sla_habilitado: boolean }>(`${BASE_URL}configuracion/sla/`, { sla_habilitado }, true);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-sla-config'] });
      const msg = res?.data?.message || 'Configuración de SLA actualizada.';
      setAlert(true, msg, AlertSeverityEnum.success);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al actualizar la configuración de SLA.';
      setAlert(true, msg, AlertSeverityEnum.error);
    },
  });
};

// ---------------------------------------------------------------------------
// SLA por Área (V1_54_0): cada área puede tener su propio SLA (toggle +
// minutos por prioridad), administrado como los árboles. Un área sin
// configuración propia usa el SLA global.
// ---------------------------------------------------------------------------

export interface ISlaAreaPrioridadRow {
  prioridad_id: string;
  codigo?: string;
  nombre?: string;
  color?: string;
  orden?: number | null;
  sla_minutos_global?: number | null;
  sla_minutos_area?: number | null;
}

export interface ISlaAreaConfig {
  area_id: string;
  configurado: boolean;
  sla_habilitado: boolean | null;
  sla_global_habilitado: boolean;
  prioridades: ISlaAreaPrioridadRow[];
}

export const useFetchSlaArea = (areaId?: string | null) => {
  return useQuery({
    queryKey: ['ticket-sla-area', areaId],
    queryFn: async () => {
      return get<ISlaAreaConfig>(`${BASE_URL}configuracion/sla/areas/${areaId}`, true);
    },
    enabled: !!areaId,
  });
};

export const useGuardarSlaArea = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore(s => s.setAlert);

  return useMutation({
    mutationFn: async (payload: {
      areaId: string;
      sla_habilitado: boolean;
      prioridades: { prioridad_id: string; sla_minutos: number }[];
    }) => {
      const { areaId, ...body } = payload;
      return put(`${BASE_URL}configuracion/sla/areas/${areaId}`, body, true);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-sla-area'] });
      const msg = res?.data?.message || 'SLA del área guardado.';
      setAlert(true, msg, AlertSeverityEnum.success);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al guardar el SLA del área.';
      setAlert(true, msg, AlertSeverityEnum.error);
    },
  });
};

export const useEliminarSlaArea = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore(s => s.setAlert);

  return useMutation({
    mutationFn: async (areaId: string) => {
      return remove(`${BASE_URL}configuracion/sla/areas/${areaId}`, true);
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-sla-area'] });
      const msg = res?.data?.message || 'El área vuelve a usar el SLA global.';
      setAlert(true, msg, AlertSeverityEnum.success);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al restablecer el SLA del área.';
      setAlert(true, msg, AlertSeverityEnum.error);
    },
  });
};
